import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Plus, Trash2, ShoppingCart, Edit, X, Truck } from "lucide-react";
import Markdown from "../components/Markdown";

const TYPES = ["weapon", "armor", "gear", "vehicle", "internal_module", "armament", "upgrade"];

const VEHICLE_TYPES = ["Ground", "Armoured", "Air/Space"];

const DEFAULT_EFFECTS = { heal: 0, stress: 0, radiation: 0, consumable: false, stat_boosts: [] };

const DEFAULTS = {
  weapon: { name:"", price:0, description:"", special:false, stats:{ damage:0, modifier:0, ammo:0, range:"", weight:0 } },
  armor:  { name:"", price:0, description:"", special:false, stats:{ level:0, modifier:0, encumbrance:0, weight:0 } },
  gear:   { name:"", price:0, description:"", special:false, stats:{ weight:0, air_power:0, effects:{...DEFAULT_EFFECTS} } },
  vehicle:{ name:"", price:0, description:"", special:false, stats:{ vehicle_type:"Ground", passengers:0, maneuverability:0, speed:0, hull:0, armor:0, weapons:"" } },
  internal_module: { name:"", price:0, description:"", special:false, stats:{ size:0 } },
  armament: { name:"", price:0, description:"", special:false, stats:{ bonus:0, range:"", size:0 } },
  upgrade: { name:"", price:0, description:"", special:false, stats:{ effect:"" } },
};

function Field({ label, children }) {
  return <div><label className="label-term mb-1 block">{label}</label>{children}</div>;
}

export default function Store() {
  const { user } = useAuth();
  const [tab, setTab] = useState("weapon");
  const [items, setItems] = useState([]);
  const [myChars, setMyChars] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [buyChar, setBuyChar] = useState({});
  const [buyTarget, setBuyTarget] = useState({}); // per-item: "char" or "camp_<id>"
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEFAULTS.weapon);
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const [r, cs, cps] = await Promise.all([
        api.get(`/shop?item_type=${tab}`),
        api.get("/characters"),
        api.get("/campaigns"),
      ]);
      setItems(r.data);
      setCampaigns(cps.data);
      // For GM/admin, allow granting to any character they can access (their own + party in their GM campaigns)
      const eligible = cs.data.filter(c => c.campaign_id && (isAdmin || c.owner_id === user?.id || cps.data.some(cp => cp.id === c.campaign_id && cp.gm_id === user?.id)));
      setMyChars(eligible);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); resetForm(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const resetForm = () => {
    setOpen(false); setEditingId(null); setPreview(false);
    setForm(JSON.parse(JSON.stringify(DEFAULTS[tab])));
  };

  const startCreate = () => {
    setEditingId(null);
    setForm(JSON.parse(JSON.stringify(DEFAULTS[tab])));
    setOpen(true);
  };

  const startEdit = (item) => {
    const base = JSON.parse(JSON.stringify(DEFAULTS[tab]));
    base.stats = { ...base.stats, ...(item.stats || {}) };
    if (tab === "gear") {
      base.stats.effects = { ...DEFAULT_EFFECTS, ...((item.stats && item.stats.effects) || {}) };
      if (!Array.isArray(base.stats.effects.stat_boosts)) base.stats.effects.stat_boosts = [];
    }
    setForm({ name: item.name, price: item.price || 0, description: item.description || "", special: !!item.special, stats: base.stats });
    setEditingId(item.id);
    setOpen(true);
  };

  const canBuyForChar = (charId) => {
    const ch = myChars.find(c => c.id === charId);
    if (!ch) return false;
    const cp = campaigns.find(c => c.id === ch.campaign_id);
    if (!cp) return false;
    if (isAdmin || cp.gm_id === user?.id) return true;
    return cp.session_active && cp.shop_open;
  };

  const myGmCampaigns = campaigns.filter(cp => isAdmin || cp.gm_id === user?.id);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, item_type: tab };
      if (editingId) {
        await api.put(`/shop/${editingId}`, payload); toast.success("Item updated");
      } else {
        await api.post("/shop", payload); toast.success("Item added");
      }
      resetForm(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this item from the store?")) return;
    try { await api.delete(`/shop/${id}`); load(); } catch { toast.error("Failed"); }
  };
  const stat = (k, v) => setForm(f => ({ ...f, stats: { ...f.stats, [k]: v } }));
  const effect = (k, v) => setForm(f => ({ ...f, stats: { ...f.stats, effects: { ...(f.stats.effects||DEFAULT_EFFECTS), [k]: v } } }));
  const boost = (idx, k, v) => setForm(f => {
    const arr = [...((f.stats.effects||DEFAULT_EFFECTS).stat_boosts || [])];
    arr[idx] = { ...arr[idx], [k]: v };
    return { ...f, stats: { ...f.stats, effects: { ...(f.stats.effects||DEFAULT_EFFECTS), stat_boosts: arr } } };
  });
  const addBoost = () => setForm(f => {
    const cur = (f.stats.effects||DEFAULT_EFFECTS).stat_boosts || [];
    return { ...f, stats: { ...f.stats, effects: { ...(f.stats.effects||DEFAULT_EFFECTS), stat_boosts: [...cur, { stat:"strength", value:1, label:"" }] } } };
  });
  const rmBoost = (idx) => setForm(f => {
    const cur = (f.stats.effects||DEFAULT_EFFECTS).stat_boosts || [];
    return { ...f, stats: { ...f.stats, effects: { ...(f.stats.effects||DEFAULT_EFFECTS), stat_boosts: cur.filter((_,i)=>i!==idx) } } };
  });

  const purchase = async (iid) => {
    const target = buyTarget[iid] || "char";
    try {
      let r;
      if (tab === "vehicle" && target.startsWith("camp_")) {
        const campId = target.slice(5);
        r = await api.post(`/shop/${iid}/purchase?target_type=campaign&campaign_id=${campId}`);
      } else {
        const cid = buyChar[iid];
        if (!cid) { toast.error("Pick a character first"); return; }
        r = await api.post(`/shop/${iid}/purchase?target_type=character&character_id=${cid}`);
      }
      toast.success(`Purchased. Party cash: $${r.data.party_cash}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Purchase failed"); }
  };

  return (
    <div className="p-4 sm:p-8" data-testid="store-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="heading-mono text-3xl sm:text-4xl amber amber-glow uppercase">// Company Store</h1>
          <div className="mono text-xs text-[#A0A0A0] mt-1 tracking-widest uppercase">Purchases deduct party cash</div>
        </div>
        {isAdmin && <button className="btn-amber flex items-center gap-2" data-testid="new-store-item-btn" onClick={startCreate}><Plus size={14}/> New {tab.replace("_"," ")}</button>}
      </div>

      <div className="flex gap-1 mb-4 border-b border-[#333] overflow-x-auto" data-testid="store-tabs">
        {TYPES.map(t => (
          <button key={t} data-testid={`store-tab-${t}`} onClick={()=>setTab(t)} className={`px-4 py-2 mono text-xs uppercase tracking-widest whitespace-nowrap ${tab===t?"amber border-b-2 border-[#FFB000]":"text-[#A0A0A0]"}`}>{t.replace("_"," ")}</button>
        ))}
      </div>

      {open && isAdmin && (
        <form onSubmit={submit} className="panel p-4 sm:p-6 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="store-create-form">
          <div className="col-span-1 sm:col-span-2 flex justify-between items-center">
            <div className="heading-mono amber uppercase">{editingId ? "// Edit" : "// New"} {tab}</div>
            <button type="button" className="amber" onClick={resetForm}><X size={16}/></button>
          </div>
          <Field label="Name"><input className="input-term" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required data-testid="store-name-input" /></Field>
          <Field label="Cost"><input className="input-term" type="number" value={form.price} onChange={e=>setForm({...form,price:Number(e.target.value)})} disabled={form.special} /></Field>
          <div className="col-span-1 sm:col-span-2">
            <label className="flex items-center gap-2 mono text-xs cursor-pointer" data-testid="special-toggle-label">
              <input type="checkbox" checked={!!form.special} onChange={e=>setForm({...form, special: e.target.checked})} data-testid="special-toggle" />
              <span className={form.special ? "text-[#FF2A2A]" : "amber"}>Special (GM only — hidden from players, cannot be purchased, must be granted)</span>
            </label>
          </div>

          {tab === "weapon" && <>
            <Field label="Damage"><input className="input-term" type="number" value={form.stats.damage} onChange={e=>stat("damage",Number(e.target.value))} /></Field>
            <Field label="Modifier"><input className="input-term" type="number" value={form.stats.modifier} onChange={e=>stat("modifier",Number(e.target.value))} /></Field>
            <Field label="Ammo"><input className="input-term" type="number" value={form.stats.ammo} onChange={e=>stat("ammo",Number(e.target.value))} /></Field>
            <Field label="Range"><input className="input-term" value={form.stats.range} onChange={e=>stat("range",e.target.value)} /></Field>
            <Field label="Weight"><input className="input-term" type="number" step="0.1" value={form.stats.weight} onChange={e=>stat("weight",Number(e.target.value))} /></Field>
          </>}

          {tab === "armor" && <>
            <Field label="Level"><input className="input-term" type="number" value={form.stats.level} onChange={e=>stat("level",Number(e.target.value))} /></Field>
            <Field label="Air Supply"><input className="input-term" type="number" value={form.stats.modifier} onChange={e=>stat("modifier",Number(e.target.value))} /></Field>
            <Field label="Weight"><input className="input-term" type="number" step="0.1" value={form.stats.weight} onChange={e=>stat("weight",Number(e.target.value))} /></Field>
          </>}

          {tab === "gear" && <>
            <Field label="Weight"><input className="input-term" type="number" step="0.1" value={form.stats.weight} onChange={e=>stat("weight",Number(e.target.value))} /></Field>
            <Field label="Air/Power"><input className="input-term" type="number" value={form.stats.air_power} onChange={e=>stat("air_power",Number(e.target.value))} /></Field>
            <div className="col-span-1 sm:col-span-2 panel p-3 mt-1 space-y-3" data-testid="effects-panel">
              <div className="label-term amber">{`// Effects (used via "Use" on character sheet)`}</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Heal HP"><input className="input-term" type="number" value={(form.stats.effects||DEFAULT_EFFECTS).heal||0} onChange={e=>effect("heal",Number(e.target.value))} /></Field>
                <Field label="Stress Δ"><input className="input-term" type="number" value={(form.stats.effects||DEFAULT_EFFECTS).stress||0} onChange={e=>effect("stress",Number(e.target.value))} /></Field>
                <Field label="Radiation Δ"><input className="input-term" type="number" value={(form.stats.effects||DEFAULT_EFFECTS).radiation||0} onChange={e=>effect("radiation",Number(e.target.value))} /></Field>
                <Field label="Consumable">
                  <select className="input-term" value={(form.stats.effects||DEFAULT_EFFECTS).consumable?"yes":"no"} onChange={e=>effect("consumable", e.target.value==="yes")}>
                    <option value="no">No</option><option value="yes">Yes (removed on use)</option>
                  </select>
                </Field>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label-term">Temp Stat Boosts (applied on use)</label>
                  <button type="button" className="btn-amber text-xs" onClick={addBoost}>+ Add Boost</button>
                </div>
                {((form.stats.effects||DEFAULT_EFFECTS).stat_boosts||[]).map((b, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                    <div className="col-span-3"><Field label="Stat">
                      <select className="input-term" value={b.stat||"strength"} onChange={e=>boost(i,"stat",e.target.value)}>
                        <option value="strength">Strength</option>
                        <option value="agility">Agility</option>
                        <option value="wits">Wits</option>
                        <option value="empathy">Empathy</option>
                        <option value="health">Health</option>
                        <option value="resolve">Resolve</option>
                      </select>
                    </Field></div>
                    <div className="col-span-2"><Field label="Value"><input className="input-term" type="number" value={b.value||0} onChange={e=>boost(i,"value",Number(e.target.value))} /></Field></div>
                    <div className="col-span-6"><Field label="Label (optional)"><input className="input-term" placeholder="e.g. Adrenaline +1 STR" value={b.label||""} onChange={e=>boost(i,"label",e.target.value)} /></Field></div>
                    <button type="button" className="text-[#FF2A2A] pb-2 col-span-1" onClick={()=>rmBoost(i)}><Trash2 size={14}/></button>
                  </div>
                ))}
              </div>
            </div>
          </>}

          {tab === "vehicle" && <>
            <Field label="Type">
              <select className="input-term" value={form.stats.vehicle_type} onChange={e=>stat("vehicle_type",e.target.value)} data-testid="vehicle-type">
                {VEHICLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Passengers"><input className="input-term" type="number" value={form.stats.passengers} onChange={e=>stat("passengers",Number(e.target.value))} /></Field>
            <Field label="Maneuverability"><input className="input-term" type="number" value={form.stats.maneuverability} onChange={e=>stat("maneuverability",Number(e.target.value))} /></Field>
            <Field label="Speed"><input className="input-term" type="number" value={form.stats.speed} onChange={e=>stat("speed",Number(e.target.value))} /></Field>
            <Field label="Hull"><input className="input-term" type="number" value={form.stats.hull} onChange={e=>stat("hull",Number(e.target.value))} /></Field>
            <Field label="Armor"><input className="input-term" type="number" value={form.stats.armor} onChange={e=>stat("armor",Number(e.target.value))} /></Field>
            <div className="col-span-1 sm:col-span-2"><Field label="Weapons"><input className="input-term" placeholder="e.g. Twin pulse cannons" value={form.stats.weapons} onChange={e=>stat("weapons",e.target.value)} /></Field></div>
          </>}

          {tab === "internal_module" && <>
            <Field label="Size"><input className="input-term" type="number" value={form.stats.size} onChange={e=>stat("size",Number(e.target.value))} /></Field>
          </>}

          {tab === "armament" && <>
            <Field label="Bonus"><input className="input-term" type="number" value={form.stats.bonus} onChange={e=>stat("bonus",Number(e.target.value))} /></Field>
            <Field label="Range"><input className="input-term" placeholder="e.g. Short/Medium/Long" value={form.stats.range} onChange={e=>stat("range",e.target.value)} /></Field>
            <Field label="Size"><input className="input-term" type="number" value={form.stats.size} onChange={e=>stat("size",Number(e.target.value))} /></Field>
          </>}

          {tab === "upgrade" && <>
            <div className="col-span-1 sm:col-span-2"><Field label="Effect"><input className="input-term" placeholder="short summary of what this upgrade does" value={form.stats.effect} onChange={e=>stat("effect",e.target.value)} /></Field></div>
          </>}

          <div className="col-span-1 sm:col-span-2">
            <div className="flex items-center justify-between">
              <label className="label-term">Description (Markdown)</label>
              <button type="button" className="btn-amber text-xs" onClick={()=>setPreview(!preview)}>{preview?"Edit":"Preview"}</button>
            </div>
            {preview ? <div className="panel p-3 mt-1"><Markdown>{form.description}</Markdown></div>
              : <textarea className="input-term min-h-[100px] mt-1" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} />}
          </div>
          <button className="btn-amber" data-testid="store-submit">{editingId ? "Save Changes" : "Create"}</button>
          <button type="button" className="btn-amber" onClick={resetForm}>Cancel</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`store-${tab}-list`}>
        {items.length === 0 && <div className="mono text-[#A0A0A0] col-span-2 p-4">// No {tab}s in stock.</div>}
        {items.map(i => {
          const ef = (i.stats||{}).effects || {};
          const boosts = ef.stat_boosts || [];
          const isSpecial = !!i.special;
          const doGrant = async () => {
            const cid = buyChar[i.id];
            const target = buyTarget[i.id] || "char";
            try {
              if (tab === "vehicle" && target.startsWith("camp_")) {
                const campId = target.slice(5);
                await api.post(`/shop/${i.id}/grant?target_type=campaign&campaign_id=${campId}`);
              } else {
                if (!cid) { toast.error("Pick a character first"); return; }
                await api.post(`/shop/${i.id}/grant?target_type=character&character_id=${cid}`);
              }
              toast.success(`Granted ${i.name}`);
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Grant failed"); }
          };
          return (
            <div key={i.id} className={`panel p-4 ${isSpecial ? "border-[#FF2A2A]" : ""}`} data-testid={`store-card-${i.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="heading-mono text-lg amber uppercase">
                    {i.name}
                    {isSpecial && <span className="ml-2 mono text-[9px] border border-[#FF2A2A] text-[#FF2A2A] px-1.5 py-0.5 tracking-widest" data-testid={`special-badge-${i.id}`}>SPECIAL</span>}
                  </div>
                  <div className="mono text-[10px] text-[#A0A0A0] uppercase tracking-widest">{tab}{tab==="vehicle" && ` · ${i.stats?.vehicle_type||"Ground"}`}</div>
                </div>
                <div className="heading-mono text-xl neon-green">{isSpecial ? "—" : `$${i.price}`}</div>
              </div>

              <div className="flex flex-wrap gap-2 mt-2 mono text-[11px]">
                {tab === "weapon" && <>
                  <span className="border border-[#333] px-2 py-0.5">DMG {i.stats?.damage ?? 0}</span>
                  <span className="border border-[#333] px-2 py-0.5">MOD {i.stats?.modifier ?? 0}</span>
                  <span className="border border-[#333] px-2 py-0.5">AMMO {i.stats?.ammo ?? 0}</span>
                  <span className="border border-[#333] px-2 py-0.5">RANGE {i.stats?.range || "—"}</span>
                </>}
                {tab === "armor" && <>
                  <span className="border border-[#333] px-2 py-0.5">LVL {i.stats?.level ?? 0}</span>
                  <span className="border border-[#333] px-2 py-0.5">AIR {i.stats?.modifier ?? 0}</span>
                </>}
                {tab === "gear" && <>
                  <span className="border border-[#333] px-2 py-0.5">AIR/PWR {i.stats?.air_power ?? 0}</span>
                  {ef.consumable && <span className="border border-[#FF2A2A] text-[#FF2A2A] px-2 py-0.5">CONSUMABLE</span>}
                  {ef.heal ? <span className="border border-[#4AF626] neon-green px-2 py-0.5">+{ef.heal} HP</span> : null}
                  {ef.stress ? <span className="border border-[#4AF626] neon-green px-2 py-0.5">{ef.stress>0?"+":""}{ef.stress} STRESS</span> : null}
                  {ef.radiation ? <span className="border border-[#4AF626] neon-green px-2 py-0.5">{ef.radiation>0?"+":""}{ef.radiation} RAD</span> : null}
                  {boosts.map((b, bi) => <span key={bi} className="border border-[#4AF626] neon-green px-2 py-0.5">{b.label || `${b.stat} ${b.value>=0?"+":""}${b.value}`}</span>)}
                </>}
                {tab === "vehicle" && <>
                  <span className="border border-[#333] px-2 py-0.5">PAX {i.stats?.passengers ?? 0}</span>
                  <span className="border border-[#333] px-2 py-0.5">MAN {i.stats?.maneuverability ?? 0}</span>
                  <span className="border border-[#333] px-2 py-0.5">SPD {i.stats?.speed ?? 0}</span>
                  <span className="border border-[#333] px-2 py-0.5">HULL {i.stats?.hull ?? 0}</span>
                  <span className="border border-[#333] px-2 py-0.5">ARM {i.stats?.armor ?? 0}</span>
                  {i.stats?.weapons && <span className="border border-[#333] px-2 py-0.5">WPN {i.stats.weapons}</span>}
                </>}
                {tab === "internal_module" && <>
                  <span className="border border-[#333] px-2 py-0.5">SIZE {i.stats?.size ?? 0}</span>
                </>}
                {tab === "armament" && <>
                  <span className="border border-[#333] px-2 py-0.5">BONUS {i.stats?.bonus ?? 0}</span>
                  <span className="border border-[#333] px-2 py-0.5">RANGE {i.stats?.range || "—"}</span>
                  <span className="border border-[#333] px-2 py-0.5">SIZE {i.stats?.size ?? 0}</span>
                </>}
                {tab === "upgrade" && i.stats?.effect && <>
                  <span className="border border-[#333] px-2 py-0.5">EFFECT {i.stats.effect}</span>
                </>}
                {(tab !== "gear" && tab !== "upgrade") && <span className="border border-[#333] px-2 py-0.5">WT {i.stats?.weight ?? 0}</span>}
              </div>

              {i.description && <div className="mt-2 text-sm"><Markdown>{i.description}</Markdown></div>}

              <div className="mt-3 space-y-2">
                {["internal_module","armament","upgrade"].includes(tab) ? (
                  <div className="mono text-[10px] text-[#A0A0A0]">// {tab.replace("_"," ")}s are equipped to the ship from the Campaign → Ship tab.</div>
                ) : (
                  <>
                {tab === "vehicle" && (
                  <select className="input-term" value={buyTarget[i.id] || "char"} onChange={e=>setBuyTarget({...buyTarget, [i.id]: e.target.value})} data-testid={`buy-target-${i.id}`}>
                    <option value="char">// To a Character</option>
                    {myGmCampaigns.map(cp => <option key={cp.id} value={`camp_${cp.id}`}>// To Party — {cp.name}</option>)}
                  </select>
                )}
                {(tab !== "vehicle" || (buyTarget[i.id] || "char") === "char") && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select className="input-term flex-1" value={buyChar[i.id] || ""} onChange={e=>setBuyChar({...buyChar, [i.id]: e.target.value})} data-testid={`buy-char-${i.id}`}>
                      <option value="">// {isSpecial ? "grant to which character..." : "for which character..."}</option>
                      {myChars.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    {isSpecial ? (
                      (isAdmin || myGmCampaigns.length > 0) && (
                        <button className="btn-amber flex items-center gap-1" style={{borderColor:"#FF2A2A", color:"#FF2A2A"}} disabled={!buyChar[i.id]} onClick={doGrant} data-testid={`grant-${i.id}`}>
                          <Truck size={12}/> Grant
                        </button>
                      )
                    ) : (
                      <button className="btn-amber flex items-center gap-1" disabled={!buyChar[i.id] || !canBuyForChar(buyChar[i.id])} onClick={()=>purchase(i.id)} data-testid={`buy-${i.id}`}>
                        <ShoppingCart size={12}/> Buy
                      </button>
                    )}
                  </div>
                )}
                {tab === "vehicle" && (buyTarget[i.id] || "").startsWith("camp_") && (
                  isSpecial ? (
                    (isAdmin || myGmCampaigns.length > 0) && (
                      <button className="btn-amber flex items-center gap-1 w-full justify-center" style={{borderColor:"#FF2A2A", color:"#FF2A2A"}} onClick={doGrant} data-testid={`grant-party-${i.id}`}>
                        <Truck size={12}/> Grant to Party
                      </button>
                    )
                  ) : (
                    <button className="btn-amber flex items-center gap-1 w-full justify-center" onClick={()=>purchase(i.id)} data-testid={`buy-party-${i.id}`}>
                      <Truck size={12}/> Buy for Party
                    </button>
                  )
                )}
                </>
                )}
                {isAdmin && (
                  <div className="flex gap-2">
                    <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>startEdit(i)} data-testid={`edit-${i.id}`}><Edit size={12}/> Edit</button>
                    <button className="text-[#FF2A2A] text-xs flex items-center gap-1 border border-[#FF2A2A] px-2 py-1" onClick={()=>del(i.id)} data-testid={`store-del-${i.id}`}><Trash2 size={12}/> Delete</button>
                  </div>
                )}
              </div>
              {!["internal_module","armament","upgrade"].includes(tab) && !isSpecial && buyChar[i.id] && !canBuyForChar(buyChar[i.id]) && (tab !== "vehicle" || (buyTarget[i.id] || "char") === "char") && (
                <div className="mono text-[10px] text-[#FF2A2A] mt-1">// store closed for this character — wait for the GM to open the store during a session</div>
              )}
              {!["internal_module","armament","upgrade"].includes(tab) && myChars.length === 0 && <div className="mono text-[10px] text-[#A0A0A0] mt-2">// character must be in a campaign to buy</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}